// PaymentRefund.js
import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import {
  Button,
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
  IconButton,
  Badge,
  Box,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiSearch,
  FiEdit,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { get_payment_refund_list } from "../../redux/paymentRefundSlice";
import DisplayData from "../../components/DisplayData";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const PaymentRefund = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState(location.state?.prevRows || 10);
  const [page, setPage] = useState(location.state?.prevPage || 1);
  const [initialValue, setInitialValue] = useState(
    location.state?.prevInitialValue || 1
  );
  const { payment_refund_list, total_refund_count,loading } = useSelector(
    (state) => state.paymentRefund
  );

  const [searchTerm, setSearchTerm] = useState(""); // 🔹 Added search state

  useEffect(() => {
    store.dispatch(get_payment_refund_list({ page, rows }));
  }, [page, rows]);

  const onPrevious = () => {
    if (page > 1) {
      setInitialValue(Number(initialValue) - Number(rows));
      setPage(page - 1);
      store.dispatch(get_payment_refund_list({ page: page - 1, rows }));
    }
  };

  const onNext = () => {
    if (rows * page < total_refund_count) {
      setInitialValue(Number(initialValue) + Number(rows));
      setPage(page + 1);
      store.dispatch(get_payment_refund_list({ page: page + 1, rows }));
    }
  };

  const getStatusBadge = (status) => {
    const statusStr = String(status);
    switch (statusStr) {
      case "0":
        return <Badge colorScheme="yellow">Pending</Badge>;
      case "1":
        return <Badge colorScheme="blue">Assigned</Badge>;
      case "2":
        return <Badge colorScheme="purple">DP Approved</Badge>;
      case "3":
        return <Badge colorScheme="green">Payment Complete</Badge>;
      case "4":
        return <Badge colorScheme="red">Rejected</Badge>;
      default:
        return <Badge colorScheme="gray">Unknown</Badge>;
    }
  };

  const calculateTotalAmount = (details) => {
    if (!details || !details.length) return "0.00";
    return details
      .reduce(
        (total, item) => total + parseFloat(item.security_deposit || 0),
        0
      )
      .toFixed(2);
  };

  const handleViewDetails = (refund, e) => {
    e.stopPropagation();
    navigate("payment-refund-detail", {
      state: {
        refund,
        prevPage: page,
        prevRows: rows,
        prevInitialValue: initialValue,
      },
    });
  };

  // 🔹 Filter logic based on name or request_id
  const filteredRefundList = payment_refund_list?.filter(
    (refund) =>
      refund.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refund.request_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Header Card */}
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading fontSize="xl" flexShrink={0}>
          Payment Refund Requests
        </Heading>

        <Spacer display={["none", "none", "block"]} />

        <Flex
          direction={["column", "row"]}
          gap={2}
          alignItems="center"
          justifyContent={["flex-start", "flex-end"]}
          flex="1"
        >
          <Select
            placeholder="Rows"
            size="md"
            w={["100%", "100px"]}
            value={rows}
            onChange={(e) => {
              setRows(e.target.value);
              setPage(1);
              setInitialValue(1);
              store.dispatch(
                get_payment_refund_list({ page: 1, rows: e.target.value })
              );
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>

          {/* 🔹 Search box */}
          <InputGroup bg="#f4f5f7" borderRadius={5} w={["100%", "auto"]} maxW="sm">
            <Input
              placeholder="Search by Name or Request ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <InputRightElement>
              <FiSearch />
            </InputRightElement>
          </InputGroup>
        </Flex>
      </Card>

      {loading ? (<GasCylinderLoader size="sm" loadingText="Loading Payment Refund"/>):(<>
        <SimpleGrid minChildWidth="xs" spacing="10px" mt={4}>
        {filteredRefundList?.length > 0 ? (
          filteredRefundList.map((refund) => {
            // Effective status for badge and edit icon
            const hasPending = refund?.details?.some(
              (d) => String(d.status) === "0"
            );
            const status = hasPending
              ? "0"
              : refund?.details?.[0]?.status ?? "0";

            return (
              <Card
                key={refund.id}
                paddingInline={5}
                paddingBlock={2} // ✅ consistent with other modules
                cursor="pointer"
                position="relative"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                _hover={{ boxShadow: "md", transform: "translateY(-3px)" }}
                transition="all 0.2s ease"
                onClick={() =>
                  navigate("payment-refund-detail", {
                    state: {
                      refund,
                      prevPage: page,
                      prevRows: rows,
                      prevInitialValue: initialValue,
                    },
                  })
                }
              >
                {/* Header & Badge */}
                <Flex justify="space-between" align="center" mb={2}>
                  <Flex align="center" gap={3}>
                    <DisplayData
                      fs="16px"
                      fw="bold"
                      v_color="#007bc9"
                      value={refund.request_id || "NA"}
                    />
                    {getStatusBadge(status)}
                  </Flex>

                  {/* Edit Icon if status === 2 */}
                  {String(status) === "2" && (
                    <IconButton
                      icon={<FiEdit />}
                      aria-label="Edit"
                      size="sm"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("payment-refund-detail", { state: { refund } });
                      }}
                    />
                  )}
                </Flex>

                {/* Details */}
                <Box mt={1}>
                  {[
                    { title: "Customer", value: refund.customer_name },
                    { title: "Reference Order ID", value: refund.orderid },
                    { title: "Refund Date", value: refund.request_date },
                    { title: "Ref. Order Date", value: refund.order_date },
                    {
                      title: "Total Amount",
                      value: `₹${calculateTotalAmount(refund.details)}`,
                    },
                  ].map((item, idx) => (
                    <Flex
                      key={idx}
                      justify="space-between"
                      align="center"
                      mb="1"
                      wrap="nowrap"
                    >
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        flex="1"
                        noOfLines={1}
                      >
                        {item.title}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.800"
                        flex="1"
                        textAlign="right"
                        noOfLines={1}
                      >
                        {item.value || "NA"}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Card>
            );
          })
        ) : (
          <Text textAlign="center" py={5}>
            No matching records found.
          </Text>
        )}
      </SimpleGrid>

      {/* Pagination */}
      <Flex justify="flex-end" mt={4}>
        <Card
          alignItems="center"
          flexDirection="row"
          gap={4}
          py={1}
          px={4}
          border="1px solid #E2E8F0"
          boxShadow="sm"
          borderRadius="md"
          bg="white"
        >
          <FiChevronLeft
            cursor="pointer"
            color={page === 1 ? "gray" : "#007bc9"}
            size={28}
            onClick={onPrevious}
          />

          <Flex align="center" gap={2} fontSize="sm" fontWeight="medium">
            <Text>{initialValue}</Text>
            <Text>-</Text>
            <Text>
              {rows * page >= total_refund_count
                ? total_refund_count
                : rows * page}
            </Text>
            <Text>of</Text>
            <Text>{total_refund_count}</Text>
          </Flex>

          <FiChevronRight
            cursor="pointer"
            color={rows * page >= total_refund_count ? "gray" : "#007bc9"}
            size={28}
            onClick={onNext}
          />
        </Card>
      </Flex>
        </>)}
      {/* List Cards */}
    
    </>
  );
};

export default PaymentRefund;
